import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const OnboardingProfile = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    display_name: user?.display_name || user?.username || '',
    bio: user?.bio || '',
    twitter_url: user?.twitter_url || '',
    linkedin_url: user?.linkedin_url || '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Update user profile
      const response = await axios.patch(`${API_URL}/users/me`, formData);
      
      // Update user state
      setUser(response.data);
      
      toast.success('Profile updated!');
      navigate('/onboarding/first-skill');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F7EFE7] to-white dark:from-dark-bg dark:to-dark-card py-12">
      <div className="max-w-2xl w-full mx-auto px-4">
        {/* Skillerate Icon */}
        <div className="flex justify-center mb-6">
          <img
            src="https://customer-assets.emergentagent.com/job_5cdedf29-6831-4187-8b3b-d6d324530c65/artifacts/vckerg2u_Skillerate%20Icon%20Only.svg"
            alt="Skillerate"
            className="h-16 w-16"
          />
        </div>
        {/* Progress indicator */}
        <div className="flex justify-center gap-2 mb-8">
          <div className="w-20 h-2 bg-gray-300 rounded-full"></div>
          <div className="w-20 h-2 bg-[#E98341] rounded-full"></div>
          <div className="w-20 h-2 bg-gray-300 rounded-full"></div>
        </div>
        
        <div className="bg-white dark:bg-dark-card rounded-2xl shadow-lg p-8">
          <div className="mb-6">
            <h2 className="text-3xl font-bold mb-2 text-[#3A1E10] dark:text-dark-text">Complete Your Profile</h2>
            <p className="text-[#6b7280] dark:text-dark-text-secondary">
              Tell us a bit about yourself (you can always change this later)
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Username - read only */}
            <div>
              <Label className="block text-sm font-semibold mb-2">
                Username
              </Label>
              <Input
                type="text"
                value={user?.username || ''}
                disabled
                className="bg-gray-100"
              />
              <p className="text-xs text-[#6b7280] mt-1">
                Your username cannot be changed
              </p>
            </div>
            
            {/* Display Name */}
            <div>
              <Label className="block text-sm font-semibold mb-2">
                Display Name
              </Label>
              <Input
                type="text"
                name="display_name"
                value={formData.display_name}
                onChange={handleChange}
                placeholder="Alex Smith"
                maxLength={50}
              />
              <p className="text-xs text-[#6b7280] mt-1">
                This is how your name will appear on your profile
              </p>
            </div>
            
            {/* Bio */}
            <div>
              <Label className="block text-sm font-semibold mb-2">
                Bio
              </Label>
              <Textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                placeholder="AI creator building Claude Skills for productivity and automation..."
                rows={3}
                maxLength={280}
              />
              <p className="text-xs text-[#6b7280] mt-1">
                {formData.bio.length}/280 characters
              </p>
            </div>
            
            {/* Twitter URL */}
            <div>
              <Label className="block text-sm font-semibold mb-2">
                Twitter/X URL <span className="text-[#6b7280] font-normal">(optional)</span>
              </Label>
              <Input
                type="url"
                name="twitter_url"
                value={formData.twitter_url}
                onChange={handleChange}
                placeholder="https://twitter.com/yourusername"
              />
            </div>
            
            {/* LinkedIn URL */}
            <div>
              <Label className="block text-sm font-semibold mb-2">
                LinkedIn URL <span className="text-[#6b7280] font-normal">(optional)</span>
              </Label>
              <Input
                type="url"
                name="linkedin_url"
                value={formData.linkedin_url}
                onChange={handleChange}
                placeholder="https://linkedin.com/in/yourusername"
              />
            </div>
            
            {/* Submit */}
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                onClick={() => navigate('/onboarding/welcome')}
                variant="outline"
                className="flex-1"
              >
                Back
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#E98341] hover:bg-[#c43408]"
              >
                {loading ? 'Saving...' : 'Continue'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OnboardingProfile;
